import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DetailsCardComponent } from '../../../components/details-card/details-card.component';
import {
  QaiLinkButtonComponent,
  QaiWordListComponent,
  QaiTextareaComponent,
  QaiLabelComponent,
  TextareaAutosizeDirective,
  WordItem,
  QaiDialogComponent,
  QaiButtonComponent,
} from '@qai/ui';
import { CommonModule } from '@angular/common';
import { AuthorService } from 'src/app/services/self-serve/author.service';
import { CallWordType } from 'src/app/types/call-word.types';
import { FormGroup, FormArray, FormControl, FormBuilder } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { AuthorReactiveFormService } from '../../../services/author-reactive-form.service';
import { SimulationConstants } from 'src/app/utils/constants/self-serve/simulation.constants';
import {
  take,
  debounceTime,
  distinctUntilChanged,
  Subscription,
  skip,
  switchMap,
  of,
} from 'rxjs';
import { IWordDefinition } from 'src/app/interfaces/self-serve/responses/iword-definition';
import { IWordsRequest } from 'src/app/interfaces/self-serve/requests/iwords-request';
import { IGetWordDetailsResponse } from 'src/app/interfaces/self-serve/responses/iget-word-details-response';
import { ISimulationPrompt } from 'src/app/interfaces/self-serve/isimulation-prompt';
import { SaveAsProductDefaultModalComponent } from '../../../components/modals/save-as-product-default-modal/save-as-product-default-modal.component';
import { IPersonaInfo } from 'src/app/interfaces/self-serve/assignment-manager/ipersona-info';

@Component({
  selector: 'app-stt-words',
  imports: [
    DetailsCardComponent,
    QaiLinkButtonComponent,
    QaiWordListComponent,
    QaiTextareaComponent,
    CommonModule,
    QaiLabelComponent,
    TextareaAutosizeDirective,
    ReactiveFormsModule,
    SaveAsProductDefaultModalComponent,
    QaiDialogComponent,
    QaiButtonComponent,
  ],
  templateUrl: './stt-words.component.html',
  styleUrl: './stt-words.component.scss',
})
export class SttWordsComponent implements OnInit, OnDestroy {
  title: string = 'STT words';
  description: string =
    'Word list supplied to Speech-to-Text (STT) providers (e.g., AssemblyAI, Tavus), specifically for STT provider configuration.';
  buttonText: string = 'Add new word';
  showTextArea: boolean = false;
  callUuid: string = '';
  moduleUuid: string = '';
  personaUuid: string = '';
  form!: FormGroup;
  sttWordsListControl: FormControl<WordItem[]> = new FormControl<WordItem[]>(
    [],
    {
      nonNullable: true,
    }
  );
  private sttWordsSubscription?: Subscription;
  private sttWordsListControlSubscription?: Subscription;
  private isUpdatingFromFormArray = signal(false);
  private isUpdatingFromAPI = signal(false);
  private wordTypeUuid: string = '';
  private previousWordsMap: Map<
    string,
    { definitionUuid: string; scope: string; scopeUuid?: string }
  > = new Map();
  private apiErrors = new Map<string, string>();
  private duplicateWords = new Set<string>();
  private readonly ERROR_DUPLICATE_WORD = 'Duplicate words are not allowed';
  private readonly DUPLICATE_WORD_STATUS = 409;
  saveAsProductDefaultModalOpen: boolean = false;
  keywordTitle: string = 'stt words';
  networkRequestInProgress: boolean = false;
  errorModalOpen: boolean = false;
  private apiRequestCount = signal(0);
  readonly isApiRequestInProgress = computed(() => this.apiRequestCount() > 0);
  @ViewChild('errorDialog') errorDialogRef: QaiDialogComponent | undefined;
  sttPromptGroup!: FormGroup;
  isGeneratingPrompt: boolean = false;
  wordsListChanged: boolean = false;
  private rootForm!: FormGroup;
  private sttPromptSubscription?: Subscription;

  constructor(
    private authorService: AuthorService,
    private route: ActivatedRoute,
    private authorReactiveFormService: AuthorReactiveFormService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {}

  get sttWordsFormArray(): FormArray {
    return this.form?.get(SimulationConstants.STT_WORDS_SECTION) as FormArray;
  }

  get hasNoWords(): boolean {
    const wordItems = this.sttWordsListControl?.value || [];
    return (
      wordItems.length === 0 ||
      wordItems.every((item) => !item.value || item.value.trim().length === 0)
    );
  }

  get hasErrors(): boolean {
    return this.apiErrors.size > 0;
  }

  get allWordsHaveProductScope(): boolean {
    if (!this.sttWordsFormArray || this.hasNoWords) {
      return false;
    }
    const words = this.sttWordsFormArray.getRawValue() as IWordDefinition[];
    if (words.length === 0) {
      return false;
    }
    return words.every(
      (word) => word?.scope === SimulationConstants.KEYWORDS_PRODUCT_SCOPE
    );
  }

  get hasProductScopedWords(): boolean {
    const hasProductScopedWordsControl = this.form?.get(
      'stt_words_hasProductScopedWords'
    );
    return hasProductScopedWordsControl?.value ?? false;
  }

  getFieldError = (
    item: WordItem,
    index: number,
    field: 'value'
  ): string | undefined => {
    const trimmedValue = item.value?.trim() || '';
    const itemId = item.uuid || trimmedValue || `index-${index}`;
    const apiError = this.apiErrors.get(itemId);

    if (apiError) {
      return apiError;
    }

    return undefined;
  };

  private updateFormControlDisabledState(): void {
    const shouldDisable = !this.hasNoWords && this.allWordsHaveProductScope;
    const wasDisabled = this.sttWordsListControl.disabled;
    
    if (shouldDisable) {
      if (!wasDisabled) {
        this.sttWordsListControl.disable({ emitEvent: false });
      }
    } else {
      if (wasDisabled) {
        const formArrayWords =
          (this.sttWordsFormArray?.getRawValue() as IWordDefinition[]) || [];
        const expectedWordItems: WordItem[] = formArrayWords
          .filter((word) => word?.key?.trim())
          .map((word) => ({
            value: word.key || '',
            uuid: word.definitionUuid || undefined,
          }));

        if (expectedWordItems.length > 0) {
          this.isUpdatingFromFormArray.set(true);
          this.sttWordsListControl.setValue(expectedWordItems, {
            emitEvent: false,
          });
          this.isUpdatingFromFormArray.set(false);
        }

        this.sttWordsListControl.enable({ emitEvent: false });
        
        if (expectedWordItems.length > 0) {
          this.isUpdatingFromFormArray.set(true);
          this.sttWordsListControl.setValue(expectedWordItems, {
            emitEvent: false,
          });
          this.isUpdatingFromFormArray.set(false);
        }
      }
    }
  }

  get sttPromptTextControl(): FormControl<string> | null {
    return this.sttPromptGroup?.get('promptText') as FormControl<string> | null;
  }

  ngOnInit(): void {
    this.callUuid = this.route.snapshot.paramMap.get('uuid') || '';
    this.loadPersonaUuid();
    this.loadModuleUuid();
    this.loadSttWords();
  }

  private loadSttWords(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form) => {
        this.rootForm = form;
        this.form = form.get(SimulationConstants.KEYWORDS_SECTION) as FormGroup;

        const wordTypeUuidControl = this.form?.get('stt_words_wordTypeUuid');
        if (wordTypeUuidControl) {
          this.wordTypeUuid = wordTypeUuidControl.value || '';
        }

        if (this.sttWordsFormArray) {
          this.syncFormArrayToControl();
          this.updatePreviousWordsMap();
          this.setupSyncBetweenFormArrayAndControl();
          this.updateFormControlDisabledState();
        }

        // Load STT prompt from form and set up auto-save
        this.loadSttPromptFromForm();
        this.setupSttPromptAutoSave();
      });
  }

  private extractKeysFromFormArray(): string[] {
    if (!this.sttWordsFormArray) return [];
    return this.authorReactiveFormService.extractKeysFromFormArray(
      this.sttWordsFormArray
    );
  }

  private syncFormArrayToControl(): void {
    if (!this.sttWordsFormArray) return;

    this.isUpdatingFromFormArray.set(true);
    const words = this.sttWordsFormArray.getRawValue() as IWordDefinition[];
    const wordItems: WordItem[] = words.map((word) => ({
      value: word.key || '',
      uuid: word.definitionUuid || undefined,
    }));

    this.sttWordsListControl.setValue(wordItems, { emitEvent: false });
    this.isUpdatingFromFormArray.set(false);
  }

  private syncWordListToFormArray(
    wordItems: WordItem[],
    skipApiProcessing: boolean = false
  ): void {
    if (!this.sttWordsFormArray) return;
    this.authorReactiveFormService.syncWordItemsToFormArray(
      this.sttWordsFormArray,
      wordItems,
      SimulationConstants.KEYWORDS_DEFAULT_SCOPE,
      SimulationConstants.STT_WORDS_SECTION,
      this.isUpdatingFromFormArray
    );
    this.updatePreviousWordsMap();

    this.cdr.detectChanges();

    if (!skipApiProcessing) {
      requestAnimationFrame(() => {
        if (!this.isUpdatingFromAPI()) {
          this.processSttWordsForAPI();
        }
      });
    }
  }

  private updatePreviousWordsMap(): void {
    if (!this.sttWordsFormArray) return;

    const currentWords =
      this.sttWordsFormArray.getRawValue() as IWordDefinition[];
    this.previousWordsMap.clear();

    currentWords.forEach((word) => {
      if (
        word.key &&
        word.definitionUuid &&
        word.scope &&
        word.definitionUuid.trim().length > 0
      ) {
        const scopeUuid = word.scopeUuid || this.getScopeUuid(word.scope);
        this.previousWordsMap.set(word.key.trim(), {
          definitionUuid: word.definitionUuid,
          scope: word.scope,
          scopeUuid: scopeUuid,
        });
      }
    });
  }

  onWordRemoved(removedWord: WordItem): void {
    if (!this.sttWordsFormArray || !removedWord) {
      return;
    }

    if (!removedWord.uuid || removedWord.uuid.trim().length === 0) {
      return;
    }

    const currentWords =
      this.sttWordsFormArray.getRawValue() as IWordDefinition[];
    const wordToDelete = currentWords.find(
      (word) => word.definitionUuid === removedWord.uuid
    );

    if (!wordToDelete) {
      return;
    }

    // Use scopeUuid from FormArray if available, otherwise calculate it
    const scopeUuid =
      wordToDelete.scopeUuid ||
      this.getScopeUuid(
        wordToDelete.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
      );

    if (!scopeUuid || scopeUuid.trim().length === 0) {
      return;
    }

    // Call delete API
    this.deleteWords([wordToDelete.definitionUuid], scopeUuid);
  }

  private deleteWords(uuids: string[], scopeValue: string): void {
    if (uuids.length === 0 || !scopeValue) {
      return;
    }

    // Check remaining words from the control (what user sees) instead of FormArray
    const currentWordItems = this.sttWordsListControl?.value || [];
    const remainingWordItems = currentWordItems.filter(
      (item) => !item.uuid || !uuids.includes(item.uuid)
    );
    const willHaveNoWords = remainingWordItems.length === 0;

    this.apiRequestCount.update((count) => count + 1);
    this.authorService
      .deleteWords({
        uuids,
        scopeValue,
      })
      .pipe(
        switchMap(() => {
          if (willHaveNoWords) {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.personaUuid
            );
          }
          return of([]);
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse | any[]) => {
          if (willHaveNoWords && Array.isArray(wordDetails)) {
            this.updateFormWithWordDetails(wordDetails, true);
            // Clear the prompt when all words are removed
            this.clearSttPrompt();
          }
          // Mark that words list has changed after successful deletion
          this.wordsListChanged = true;
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
        error: (error: any) => {
          console.error('Error deleting words:', error);
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  private setupSyncBetweenFormArrayAndControl(): void {
    if (this.sttWordsSubscription) {
      this.sttWordsSubscription.unsubscribe();
    }
    if (this.sttWordsListControlSubscription) {
      this.sttWordsListControlSubscription.unsubscribe();
    }

    this.sttWordsSubscription = this.sttWordsFormArray.valueChanges
      .pipe(
        skip(1),
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe(() => {
        if (!this.sttWordsFormArray) return;

        if (this.isUpdatingFromAPI()) {
          return;
        }

        if (this.isUpdatingFromFormArray()) {
          return;
        }

        this.personaUuid = this.getPersonaUuidFromForm();

        this.syncFormArrayToControl();
        this.updateFormControlDisabledState();
        this.processSttWordsForAPI();
      });

    this.sttWordsListControlSubscription = this.sttWordsListControl.valueChanges
      .pipe(
        skip(1),
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((wordItems: WordItem[]) => {
        if (this.isUpdatingFromAPI()) {
          return;
        }

        if (this.isUpdatingFromFormArray()) {
          return;
        }

        if (wordItems.length === 1 && (!wordItems[0].value || wordItems[0].value.trim().length === 0)) {
          const formArrayWords =
            (this.sttWordsFormArray?.getRawValue() as IWordDefinition[]) || [];
          const hasApiWords = formArrayWords.some((word) => word?.definitionUuid);
          if (hasApiWords) {
            this.syncFormArrayToControl();
          }
          return;
        }

        if (this.apiErrors.size > 0) {
          this.clearErrorsForModifiedWords(wordItems);
          this.syncWordListToFormArray(wordItems, true);
          this.validateAndUpdateErrors();

          if (this.apiErrors.size === 0) {
            this.sttWordsListControl.updateValueAndValidity();
          } else {
            return;
          }
        }

        const hasIncompleteFields = wordItems.some(
          (item) => !item.value || item.value.trim().length === 0
        );

        if (hasIncompleteFields) {
          this.syncWordListToFormArray(wordItems);
          this.updateFormControlDisabledState();
          this.syncWordListToFormArray(wordItems, true);
          return;
        }

        this.syncWordListToFormArray(wordItems);
        this.updateFormControlDisabledState();
      });
  }

  private getPersonaUuidFromForm(): string {
    const rootForm = this.rootForm || this.form?.parent;
    if (!rootForm) return '';
    const generalSection = rootForm.get(
      SimulationConstants.GENERAL_SECTION
    ) as FormGroup;
    const callPersonaGroup = generalSection?.get(
      SimulationConstants.CALL_PERSONA_SECTION
    ) as FormGroup;
    const personaUuidControl = callPersonaGroup?.get('uuid');
    return personaUuidControl?.value || '';
  }

  private updatePersonaIfSelected(): void {
    const personaUuid = this.getPersonaUuidFromForm();
    if (!personaUuid) {
      return; // No persona selected, skip
    }

    // Get persona data from form
    if (!this.rootForm) return;
    const generalSection = this.rootForm.get(
      SimulationConstants.GENERAL_SECTION
    ) as FormGroup;
    const callPersonaGroup = generalSection?.get(
      SimulationConstants.CALL_PERSONA_SECTION
    ) as FormGroup;

    if (!callPersonaGroup) {
      return;
    }

    const personaId = callPersonaGroup.get('personaId')?.value;
    if (!personaId) {
      return; // Persona not fully created yet, skip
    }

    // Build payload with only personaId and callUuid
    const payload: Partial<IPersonaInfo> = {
      personaId,
      callUuid: this.callUuid,
    };

    // Call update API
    this.authorService.updatePersonaInfo(payload as IPersonaInfo).subscribe({
      next: (response) => {
        console.log('Persona updated after prompt change:', response);
      },
      error: (error) => {
        console.error('Error updating persona after prompt change:', error);
      },
    });
  }

  private getScopeUuid(scope: string): string {
    switch (scope) {
      case SimulationConstants.KEYWORDS_PRODUCT_SCOPE:
        return this.moduleUuid;
      case SimulationConstants.KEYWORDS_PERSONA_SCOPE:
        return this.getPersonaUuidFromForm();
      case SimulationConstants.KEYWORDS_DEFAULT_SCOPE:
      default:
        return this.callUuid;
    }
  }

  private transformWordsToRequest(
    words: IWordDefinition[],
    includeUuid: boolean = false
  ): IWordsRequest {
    const validWords = words.filter(
      (word) => word.key && word.key.trim().length > 0
    );

    const filteredWords = includeUuid
      ? validWords.filter(
        (word) => word.definitionUuid && word.definitionUuid.trim().length > 0
      )
      : validWords;

    let wordTypeUuidToUse = this.wordTypeUuid;
    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      const wordTypeUuidControl = this.form?.get('stt_words_wordTypeUuid');
      wordTypeUuidToUse = wordTypeUuidControl?.value || '';
    }
    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      wordTypeUuidToUse =
        filteredWords.find(
          (word) => word.wordTypeUuid && word.wordTypeUuid.trim().length > 0
        )?.wordTypeUuid || '';
    }

    if (!wordTypeUuidToUse || wordTypeUuidToUse.trim().length === 0) {
      return { wordTypeEntries: [] };
    }

    const wordsWithTypeUuid = filteredWords.map((word) => ({
      ...word,
      wordTypeUuid: word.wordTypeUuid || wordTypeUuidToUse,
    }));

    const wordsByType = new Map<string, IWordDefinition[]>();
    wordsWithTypeUuid.forEach((word) => {
      const wordTypeUuid = word.wordTypeUuid!.trim();
      if (!wordsByType.has(wordTypeUuid)) {
        wordsByType.set(wordTypeUuid, []);
      }
      wordsByType.get(wordTypeUuid)!.push(word);
    });

    const wordTypeEntries = Array.from(wordsByType.entries()).map(
      ([wordTypeUuid, typeWords]) => {
        const firstWord = typeWords[0];
        const scope =
          firstWord.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE;

        return {
          wordTypeUuid: wordTypeUuid,
          scope,
          scopeUuid: this.getScopeUuid(scope),
          words: typeWords.map((word) => {
            const entry: {
              uuid?: string;
              wordKey: string;
              wordValue?: string | null;
            } = {
              wordKey: word.key.trim(),
            };

            if (includeUuid && word.definitionUuid) {
              entry.uuid = word.definitionUuid;
            }

            if (word.value) {
              entry.wordValue = word.value;
            }

            return entry;
          }),
        };
      }
    );

    return { wordTypeEntries };
  }

  private updateFormWithWordDetails(
    wordDetails: IGetWordDetailsResponse,
    forceReplace: boolean = false
  ): void {
    if (!wordDetails || !Array.isArray(wordDetails)) {
      return;
    }

    const sttWordsGroup = wordDetails.find(
      (group) => group.wordTypeName === SimulationConstants.STT_WORDS_SECTION
    );

    if (!sttWordsGroup) {
      return;
    }

    if (sttWordsGroup.wordTypeUuid) {
      this.wordTypeUuid = sttWordsGroup.wordTypeUuid;
      const wordTypeUuidControl = this.form?.get('stt_words_wordTypeUuid');
      if (wordTypeUuidControl) {
        wordTypeUuidControl.setValue(sttWordsGroup.wordTypeUuid, {
          emitEvent: false,
        });
      }
    }

    if (sttWordsGroup.hasProductScopedWords !== undefined) {
      const hasProductScopedWordsControl = this.form?.get(
        'stt_words_hasProductScopedWords'
      );
      if (hasProductScopedWordsControl) {
        hasProductScopedWordsControl.setValue(
          sttWordsGroup.hasProductScopedWords,
          { emitEvent: false }
        );
      }
    }

    if (!sttWordsGroup.words || sttWordsGroup.words.length === 0) {
      this.isUpdatingFromAPI.set(true);

      if (this.sttWordsFormArray) {
        while (this.sttWordsFormArray.length > 0) {
          this.sttWordsFormArray.removeAt(0, { emitEvent: false });
        }
      }

      this.sttWordsListControl.setValue([], { emitEvent: false });
      this.isUpdatingFromAPI.set(false);
      return;
    }

    this.isUpdatingFromAPI.set(true);

    if (!this.sttWordsFormArray) {
      this.isUpdatingFromAPI.set(false);
      return;
    }

    const apiWords: IWordDefinition[] = sttWordsGroup.words.map((word) => ({
      key: word.wordKey,
      value: word.wordValue,
      scope: word.scope,
      typeName: sttWordsGroup.wordTypeName,
      definitionUuid: word.uuid,
      wordTypeUuid: sttWordsGroup.wordTypeUuid,
      scopeUuid: word.scopeUuid,
    }));

    if (forceReplace) {
      this.sttWordsFormArray.clear({ emitEvent: false });
      apiWords.forEach((word) => {
        this.sttWordsFormArray.push(
          this.fb.group({
            key: [word.key],
            value: [word.value],
            scope: [word.scope],
            typeName: [word.typeName],
            definitionUuid: [word.definitionUuid],
            wordTypeUuid: [word.wordTypeUuid],
            scopeUuid: [
              word.scopeUuid ||
              this.getScopeUuid(
                word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
              ),
            ],
          }),
          { emitEvent: false }
        );
      });

      this.syncFormArrayToControl();
      this.updatePreviousWordsMap();

      this.cdr.detectChanges();
      requestAnimationFrame(() => {
        this.updateFormControlDisabledState();
        this.isUpdatingFromAPI.set(false);
      });
      return;
    }

    if (this.sttWordsListControl && this.sttWordsFormArray) {
      const currentWordItems = this.sttWordsListControl.value || [];
      this.syncWordListToFormArray(currentWordItems, true);
    }

    const currentFormWords =
      this.sttWordsFormArray.getRawValue() as IWordDefinition[];
    const incompleteFields = currentFormWords.filter(
      (w) => !w.key?.trim() || !w.key
    );
    const apiWordsByUuid = new Map(
      apiWords
        .filter((w) => w.definitionUuid)
        .map((w) => [w.definitionUuid!, w])
    );
    const apiWordsByKey = new Map(
      apiWords.filter((w) => w.key?.trim()).map((w) => [w.key!.trim(), w])
    );
    const matchedApiWords = new Set<string>();

    for (let i = 0; i < this.sttWordsFormArray.length; i++) {
      const formGroup = this.sttWordsFormArray.at(i) as FormGroup;
      const word = formGroup.getRawValue() as IWordDefinition;
      const k = word.key?.trim() || '';
      const matched =
        word.definitionUuid && apiWordsByUuid.has(word.definitionUuid)
          ? apiWordsByUuid.get(word.definitionUuid)
          : k && apiWordsByKey.has(k)
            ? apiWordsByKey.get(k)
            : undefined;

      if (matched) {
        if (word.definitionUuid) matchedApiWords.add(word.definitionUuid);
        if (k) matchedApiWords.add(k);

        const fieldsToUpdate = [
          'definitionUuid',
          'wordTypeUuid',
          'scopeUuid',
          'scope',
          'typeName',
        ];

        if (word.definitionUuid && matched.key) {
          fieldsToUpdate.push('key');
        }

        fieldsToUpdate.forEach((field) => {
          const control = formGroup.get(field);
          if (control && matched[field as keyof IWordDefinition]) {
            control.setValue(matched[field as keyof IWordDefinition], {
              emitEvent: false,
            });
          }
        });
      }
    }

    const existing = new Set(
      (this.sttWordsFormArray.getRawValue() as IWordDefinition[])
        .map((w) => w.key?.trim())
        .filter((k): k is string => !!k)
    );
    const apiKeys = new Set(apiWords.map((w) => w.key?.trim()).filter(Boolean));

    apiWords.forEach((word) => {
      const k = word.key?.trim() || '';
      if (!k) return;
      if (word.definitionUuid && matchedApiWords.has(word.definitionUuid))
        return;
      if (matchedApiWords.has(k)) return;
      if (existing.has(k)) return;
      this.sttWordsFormArray.push(
        this.fb.group({
          key: [word.key],
          value: [word.value],
          scope: [word.scope],
          typeName: [word.typeName],
          definitionUuid: [word.definitionUuid],
          wordTypeUuid: [word.wordTypeUuid],
          scopeUuid: [
            word.scopeUuid ||
            this.getScopeUuid(
              word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
            ),
          ],
        }),
        { emitEvent: false }
      );
      existing.add(k);
    });

    const currentFormWordsAfterApiAdditions =
      this.sttWordsFormArray.getRawValue() as IWordDefinition[];
    const existingAfterAdditions = new Set(
      currentFormWordsAfterApiAdditions.map((w) => w.key?.trim() || '')
    );

    incompleteFields.forEach((word) => {
      const k = word.key?.trim() || '';
      if (k && apiKeys.has(k)) return;

      const wordKey = word.key || '';
      const alreadyExists = currentFormWordsAfterApiAdditions.some(
        (w) => (w.key || '') === wordKey && !w.key?.trim()
      );

      if (alreadyExists) return;

      this.sttWordsFormArray.push(
        this.fb.group({
          key: [word.key || ''],
          value: [word.value || null],
          scope: [word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE],
          typeName: [word.typeName || sttWordsGroup.wordTypeName],
          definitionUuid: [word.definitionUuid || ''],
          wordTypeUuid: [word.wordTypeUuid || sttWordsGroup.wordTypeUuid],
          scopeUuid: [
            word.scopeUuid ||
            this.getScopeUuid(
              word.scope || SimulationConstants.KEYWORDS_DEFAULT_SCOPE
            ),
          ],
        }),
        { emitEvent: false }
      );
    });

    this.syncFormArrayToControl();
    this.updatePreviousWordsMap();
    this.updateFormControlDisabledState();

    this.cdr.detectChanges();
    requestAnimationFrame(() => {
      this.isUpdatingFromAPI.set(false);
    });
  }

  private createKeyword(words: IWordDefinition[]): void {
    if (!this.callUuid || words.length === 0) {
      return;
    }

    const payload = this.transformWordsToRequest(words, false);

    if (payload.wordTypeEntries.length === 0) {
      console.warn('No valid word type entries to create');
      return;
    }

    this.apiRequestCount.update((count) => count + 1);
    this.authorService
      .createWords(payload)
      .pipe(
        switchMap(() => {
          return this.authorService.getWordDetails(
            this.callUuid,
            this.personaUuid
          );
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse) => {
          console.log(
            'createKeyword successful, updating form with word details'
          );
          // Mark that words list has changed after successful creation
          this.wordsListChanged = true;
          words.forEach((word) => {
            const itemId = word.definitionUuid || word.key?.trim() || '';
            this.apiErrors.delete(itemId);
          });
          this.updateFormWithWordDetails(wordDetails, false);
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
        error: (error: any) => {
          console.error(
            'Error creating keywords or fetching word details:',
            error
          );
          if (error?.status === this.DUPLICATE_WORD_STATUS) {
            this.handleDuplicateError(words);
          }
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  private updateKeyword(words: IWordDefinition[]): void {
    if (!this.callUuid || words.length === 0) {
      return;
    }

    const payload = this.transformWordsToRequest(words, true);

    if (payload.wordTypeEntries.length === 0) {
      console.warn('No valid word type entries to update');
      return;
    }

    this.apiRequestCount.update((count) => count + 1);
    this.authorService.updateWords(payload).subscribe({
      next: (response: any) => {
        words.forEach((word) => {
          const itemId = word.definitionUuid || word.key?.trim() || '';
          const error = this.apiErrors.get(itemId);
          if (error && error !== this.ERROR_DUPLICATE_WORD) {
            this.apiErrors.delete(itemId);
          }
        });
        this.sttWordsListControl.updateValueAndValidity();
        this.apiRequestCount.update((count) => Math.max(0, count - 1));
      },
      error: (error: any) => {
        console.error('Error updating keywords:', error);
        if (error?.status === this.DUPLICATE_WORD_STATUS) {
          this.handleDuplicateError(words);
        }
        this.apiRequestCount.update((count) => Math.max(0, count - 1));
      },
    });
  }

  private processSttWordsForAPI(): void {
    if (!this.sttWordsFormArray) return;

    const sttWords = this.sttWordsFormArray.getRawValue() as IWordDefinition[];

    const validWords = sttWords.filter(
      (word) => word.key && word.key.trim().length > 0
    );

    if (validWords.length === 0) {
      return;
    }

    const wordsToUpdate: IWordDefinition[] = [];
    const wordsToCreate: IWordDefinition[] = [];
    const processedUuids = new Set<string>();
    const processedKeys = new Set<string>();

    validWords.forEach((word) => {
      const trimmedKey = word.key.trim();

      if (word.definitionUuid && word.definitionUuid.trim().length > 0) {
        if (processedUuids.has(word.definitionUuid)) {
          wordsToCreate.push({ ...word, definitionUuid: '' });
          return;
        }
        wordsToUpdate.push(word);
        processedUuids.add(word.definitionUuid);
      } else {
        if (processedKeys.has(trimmedKey)) {
          return;
        }

        wordsToCreate.push(word);
        processedKeys.add(trimmedKey);
      }
    });

    if (wordsToUpdate.length > 0) {
      this.updateKeyword(wordsToUpdate);
    }

    if (wordsToCreate.length > 0) {
      this.createKeyword(wordsToCreate);
    }
  }

  expandTextArea(event: Event): void {
    this.showTextArea = true;
  }

  onGenerateSttPrompt(): void {
    if (!this.callUuid || this.isGeneratingPrompt) {
      return;
    }

    this.isGeneratingPrompt = true;
    this.apiRequestCount.update((count) => count + 1);

    this.authorService.generateSttPrompt(this.callUuid).subscribe({
      next: (response: ISimulationPrompt) => {
        if (this.sttPromptGroup) {
          // Update form with generated prompt
          this.sttPromptGroup.patchValue(
            {
              promptText: response.promptText || '',
              uuid: response.uuid || null,
              promptType: SimulationConstants.STT_PROMPT_TYPE,
              weightPercentage: 0,
            },
            { emitEvent: false }
          );
        }
        this.showTextArea = true;
        this.isGeneratingPrompt = false;
        // Reset words list changed flag after successful generation
        this.wordsListChanged = false;
        // Update persona if selected
        this.updatePersonaIfSelected();
        this.apiRequestCount.update((count) => Math.max(0, count - 1));
      },
      error: (error: any) => {
        console.error('Error generating STT prompt:', error);
        this.isGeneratingPrompt = false;
        this.apiRequestCount.update((count) => Math.max(0, count - 1));
      },
    });
  }

  onResetToProductDefault(): void {
    if (this.callUuid) {
      const wordType: CallWordType = 'stt_words';
      this.apiRequestCount.update((count) => count + 1);
      this.authorService
        .resetCallKeywords(this.callUuid, wordType)
        .pipe(
          switchMap(() => {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.personaUuid
            );
          })
        )
        .subscribe({
          next: (wordDetails: IGetWordDetailsResponse) => {
            this.updateFormWithWordDetails(wordDetails, true);
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
          error: (error: any) => {
            console.error(
              'Error resetting to product default or fetching word details:',
              error
            );
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
        });
    }
  }

  saveAsProductDefault(): void {
    this.errorModalOpen = true;
    setTimeout(() => {
      this.errorDialogRef?.showModal();
    }, 0);
  }

  onSaveAsProductDefaultConfirm(): void {
    if (!this.callUuid || !this.moduleUuid) {
      console.error('Missing callUuid or moduleUuid');
      return;
    }

    this.networkRequestInProgress = true;
    this.apiRequestCount.update((count) => count + 1);

    this.authorService
      .saveAsProductDefault({
        moduleUuid: this.moduleUuid,
        wordType: 'stt_words' as CallWordType,
        callUuid: this.callUuid,
        personaUuid: this.personaUuid,
      })
      .pipe(
        switchMap(() => {
          return this.authorService.getWordDetails(
            this.callUuid,
            this.personaUuid
          );
        })
      )
      .subscribe({
        next: (wordDetails: IGetWordDetailsResponse) => {
          console.log(
            'Save as product default successful, updating form with word details'
          );
          this.updateFormWithWordDetails(wordDetails, true);
          this.networkRequestInProgress = false;
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
          this.saveAsProductDefaultModalOpen = false;
        },
        error: (error: any) => {
          console.error(
            'Error saving as product default or fetching word details:',
            error
          );
          this.networkRequestInProgress = false;
          this.apiRequestCount.update((count) => Math.max(0, count - 1));
        },
      });
  }

  onSaveAsProductDefaultModalClose(): void {
    this.saveAsProductDefaultModalOpen = false;
  }

  onErrorModalClose(): void {
    this.errorModalOpen = false;
    this.errorDialogRef?.close();
  }

  onErrorModalAfterClose(): void {
    this.errorModalOpen = false;
  }

  onDetachFromProductDefault(): void {
    if (this.callUuid && this.moduleUuid) {
      this.apiRequestCount.update((count) => count + 1);
      this.authorService
        .cloneModuleKeywords({
          callUuid: this.callUuid,
          moduleUuid: this.moduleUuid,
          wordType: 'stt_words' as CallWordType,
        })
        .pipe(
          switchMap(() => {
            return this.authorService.getWordDetails(
              this.callUuid,
              this.personaUuid
            );
          })
        )
        .subscribe({
          next: (wordDetails: IGetWordDetailsResponse) => {
            console.log(
              'Detach from product default successful, updating form with word details'
            );
            this.updateFormWithWordDetails(wordDetails, true);
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
          error: (error: any) => {
            console.error(
              'Error detaching from product default or fetching word details:',
              error
            );
            this.apiRequestCount.update((count) => Math.max(0, count - 1));
          },
        });
    }
  }

  private loadPersonaUuid(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form: FormGroup) => {
        const generalSection = form.get(
          SimulationConstants.GENERAL_SECTION
        ) as FormGroup;
        const callPersonaGroup = generalSection?.get(
          SimulationConstants.CALL_PERSONA_SECTION
        ) as FormGroup;
        const personaUuidControl = callPersonaGroup?.get('uuid');
        if (personaUuidControl) {
          this.personaUuid = personaUuidControl.value || '';
        }
      });
  }

  private loadModuleUuid(): void {
    this.authorReactiveFormService
      .getForm(this.callUuid)
      .pipe(take(1))
      .subscribe((form: FormGroup) => {
        const generalSection = form.get(
          SimulationConstants.GENERAL_SECTION
        ) as FormGroup;
        const moduleUuidControl = generalSection?.get('moduleUuid');
        if (moduleUuidControl) {
          this.moduleUuid = moduleUuidControl.value || '';
        }
      });
  }

  private handleDuplicateError(words: IWordDefinition[]): void {
    const allFormWords =
      (this.sttWordsFormArray?.getRawValue() as IWordDefinition[]) || [];
    const duplicateWordsSet = this.detectDuplicateWords(words, allFormWords);

    if (duplicateWordsSet.size > 0) {
      duplicateWordsSet.forEach((word) => this.duplicateWords.add(word));
      this.setErrorsOnDuplicateWords(duplicateWordsSet, allFormWords, words);
    }

    this.sttWordsListControl.updateValueAndValidity();
  }

  private setErrorsOnDuplicateWords(
    duplicateWordsSet: Set<string>,
    allFormWords: IWordDefinition[],
    requestWords: IWordDefinition[]
  ): void {
    const allWordsToCheck = [...allFormWords, ...requestWords];

    duplicateWordsSet.forEach((duplicateWord) => {
      allWordsToCheck.forEach((word) => {
        const trimmedKey = word.key?.trim() || '';
        if (trimmedKey === duplicateWord) {
          const itemId = word.definitionUuid || trimmedKey;
          this.apiErrors.set(itemId, this.ERROR_DUPLICATE_WORD);
        }
      });
    });
  }

  private detectDuplicateWords(
    words: IWordDefinition[],
    allFormWords?: IWordDefinition[]
  ): Set<string> {
    const duplicates = new Set<string>();
    const formWords =
      allFormWords && allFormWords !== words
        ? allFormWords
        : (this.sttWordsFormArray?.getRawValue() as IWordDefinition[]) || [];

    const wordCounts = new Map<string, number>();

    formWords.forEach((w) => {
      const trimmedKey = w.key?.trim() || '';
      if (trimmedKey) {
        wordCounts.set(trimmedKey, (wordCounts.get(trimmedKey) || 0) + 1);
      }
    });

    wordCounts.forEach((count, word) => {
      if (count > 1) {
        duplicates.add(word);
      }
    });

    return duplicates;
  }

  private validateAndUpdateErrors(): void {
    const currentWords =
      (this.sttWordsFormArray?.getRawValue() as IWordDefinition[]) || [];
    const controlWords = this.sttWordsListControl?.value || [];

    const allWords: IWordDefinition[] = [...currentWords];
    controlWords.forEach((item) => {
      const trimmedValue = item.value?.trim() || '';
      if (trimmedValue) {
        const existsInFormArray = currentWords.some(
          (w) => w.key?.trim() === trimmedValue
        );
        if (!existsInFormArray) {
          allWords.push({
            key: trimmedValue,
            value: null,
            definitionUuid: item.uuid || '',
          } as IWordDefinition);
        }
      }
    });

    const duplicateWordsSet = this.detectDuplicateWords(allWords);

    const currentWordValues = new Set<string>();
    const currentItemIds = new Map<string, string>();

    currentWords.forEach((word) => {
      const trimmedKey = word.key?.trim() || '';
      if (trimmedKey) {
        currentWordValues.add(trimmedKey);
        const itemId = word.definitionUuid || trimmedKey;
        currentItemIds.set(itemId, trimmedKey);
      }
    });

    controlWords.forEach((item) => {
      const trimmedValue = item.value?.trim() || '';
      if (trimmedValue) {
        const itemId = item.uuid || trimmedValue;
        if (!currentItemIds.has(itemId)) {
          currentItemIds.set(itemId, trimmedValue);
        }
      }
    });

    const itemsToClear: string[] = [];
    this.apiErrors.forEach((error, itemId) => {
      const wordValue = currentItemIds.get(itemId);
      const exists = wordValue !== undefined;
      const isDuplicate = wordValue ? duplicateWordsSet.has(wordValue) : false;

      if (!exists) {
        itemsToClear.push(itemId);
      } else if (!isDuplicate && error === this.ERROR_DUPLICATE_WORD) {
        itemsToClear.push(itemId);
      }
    });

    itemsToClear.forEach((itemId) => this.apiErrors.delete(itemId));

    this.duplicateWords.clear();
    duplicateWordsSet.forEach((word) => this.duplicateWords.add(word));
  }

  private clearErrorsForModifiedWords(currentWordItems: WordItem[]): void {
    const currentItemIds = new Set<string>();

    currentWordItems.forEach((item) => {
      const trimmedValue = item.value?.trim() || '';
      if (trimmedValue) {
        const itemId = item.uuid || trimmedValue;
        currentItemIds.add(itemId);
      }
    });

    const itemsToClear: string[] = [];
    this.apiErrors.forEach((error, itemId) => {
      if (error === this.ERROR_DUPLICATE_WORD) {
        return;
      }
      if (!currentItemIds.has(itemId)) {
        itemsToClear.push(itemId);
      }
    });

    itemsToClear.forEach((itemId) => this.apiErrors.delete(itemId));
  }

  private loadSttPromptFromForm(): void {
    if (!this.rootForm) {
      return;
    }

    const contentSection = this.rootForm.get(
      SimulationConstants.CONTENT_SECTION
    ) as FormGroup;
    this.sttPromptGroup = contentSection?.get('sttPrompt') as FormGroup;

    if (this.sttPromptGroup) {
      const sttPrompt = this.sttPromptGroup.value as ISimulationPrompt;
      // Show textarea if prompt text exists
      if (sttPrompt?.promptText) {
        this.showTextArea = true;
      }
    }
  }

  private setupSttPromptAutoSave(): void {
    if (!this.sttPromptGroup) {
      return;
    }

    if (this.sttPromptSubscription) {
      this.sttPromptSubscription.unsubscribe();
    }

    const promptTextControl = this.sttPromptGroup.get('promptText');
    if (!promptTextControl) {
      return;
    }

    let previousValue: string = promptTextControl.getRawValue();

    this.sttPromptSubscription = promptTextControl.valueChanges
      .pipe(
        skip(1), // ignore initial form init change
        debounceTime(SimulationConstants.AUTOSAVE_GENERIC_DEBOUNCE),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      )
      .subscribe((newVal) => {
        const oldValue = previousValue;
        previousValue = newVal;

        const updatedValue =
          this.sttPromptGroup.getRawValue() as ISimulationPrompt;

        const trimmedOld = (oldValue || '').trim();
        const trimmedNew = (newVal || '').trim();

        // Don't save if values are the same or both empty
        if (trimmedOld === trimmedNew && trimmedNew === '') {
          return;
        }

        // Update form in service
        this.authorReactiveFormService.patchForm(
          this.callUuid,
          SimulationConstants.STT_PROMPT_PATH,
          updatedValue
        );

        // Save to API
        if (updatedValue) {
          this.triggerPromptSave(this.callUuid, updatedValue);
        }
      });
  }

  private triggerPromptSave(callUuid: string, prompt: ISimulationPrompt): void {
    if (!callUuid) {
      return;
    }

    const payload = { prompts: [prompt] };

    const request$ = prompt.uuid
      ? this.authorService.updateSimulationPrompts(callUuid, payload.prompts)
      : this.authorService.createSimulationPrompts(callUuid, payload.prompts);

    request$.subscribe({
      next: (response) => {
        const updatedPrompt = response?.prompts?.[0];
        if (updatedPrompt?.uuid) {
          // Update uuid in form
          this.authorReactiveFormService.patchForm(
            callUuid,
            SimulationConstants.STT_PROMPT_PATH,
            {
              uuid: updatedPrompt.promptText === '' ? null : updatedPrompt.uuid,
            }
          );
          console.log('STT prompt saved successfully');
        }
        // Update persona if selected
        this.updatePersonaIfSelected();
      },
      error: (error: any) => {
        console.error('Error saving STT prompt:', error);
      },
    });
  }

  private clearSttPrompt(): void {
    if (!this.sttPromptGroup || !this.callUuid) {
      return;
    }

    const currentPrompt = this.sttPromptGroup.getRawValue() as ISimulationPrompt;

    // Only clear if prompt exists (has uuid) and has text
    if (!currentPrompt?.uuid || !currentPrompt.promptText?.trim()) {
      return;
    }

    // Create prompt with empty text
    const emptyPrompt: ISimulationPrompt = {
      ...currentPrompt,
      promptText: '',
    };

    // Update form first
    this.sttPromptGroup.patchValue(
      {
        promptText: '',
      },
      { emitEvent: false }
    );

    // Hide textarea
    this.showTextArea = false;

    // Call updatePrompts with empty string
    this.authorService
      .updateSimulationPrompts(this.callUuid, [emptyPrompt])
      .subscribe({
        next: (response) => {
          const updatedPrompt = response?.prompts?.[0];
          if (updatedPrompt) {
            // Update uuid in form (set to null when promptText is empty)
            this.authorReactiveFormService.patchForm(
              this.callUuid,
              SimulationConstants.STT_PROMPT_PATH,
              {
                uuid: updatedPrompt.promptText === '' ? null : updatedPrompt.uuid,
              }
            );
            console.log('STT prompt cleared successfully');
          }
          // Update persona if selected
          this.updatePersonaIfSelected();
        },
        error: (error: any) => {
          console.error('Error clearing STT prompt:', error);
        },
      });
  }

  ngOnDestroy(): void {
    if (this.sttWordsSubscription) {
      this.sttWordsSubscription.unsubscribe();
    }
    if (this.sttWordsListControlSubscription) {
      this.sttWordsListControlSubscription.unsubscribe();
    }
    if (this.sttPromptSubscription) {
      this.sttPromptSubscription.unsubscribe();
    }
  }
}
